import {
  FILTER_ORDER,
  FILTER_REGISTRY,
  resolve,
  type ResolvedFilters,
} from "./filters";

export type MergeTagBuilder = (
  identifier: string,
  filters?: ResolvedFilters,
) => string;

const NESTED_TILE_DATA_PAYLOAD_PATTERN =
  /^tile_data\.(?:metadata|event_data)\..+/;

function nestedTileDataParentPaths(identifier: string): string[] {
  if (!NESTED_TILE_DATA_PAYLOAD_PATTERN.test(identifier)) return [];

  const segments = identifier.split(".");
  const parents: string[] = [];
  for (let length = 2; length < segments.length; length += 1) {
    parents.push(segments.slice(0, length).join("."));
  }
  return parents;
}

function wrapNestedTileDataAccess(
  identifier: string,
  inner: string,
  fallback: string | undefined,
): string {
  const parents = nestedTileDataParentPaths(identifier);
  if (parents.length === 0) return inner;

  const conditions =
    fallback === undefined ? parents : [...parents, identifier];
  return `{% if ${conditions.join(" and ")} %}${inner}{% else %}${
    fallback ?? ""
  }{% endif %}`;
}

/**
 * Current wire syntax: `{{<id>|<filters>}}` with the filter chain in
 * {@link FILTER_ORDER} (e.g. `{{customer.first_name|default:"there"|capfirst}}`).
 */
export const liquidFilterChainBuilder: MergeTagBuilder = (
  identifier,
  filters,
) => {
  const resolved = resolve(filters);
  const segments: string[] = [identifier];

  for (const id of FILTER_ORDER) {
    const value = resolved[id];
    if (value === undefined) continue;
    const segment = FILTER_REGISTRY[id].build(value);
    if (segment) segments.push(segment);
  }
  segments.push(...(resolved.passthrough ?? []));

  return wrapNestedTileDataAccess(
    identifier,
    `{{${segments.join("|")}}}`,
    typeof resolved.default === "string" ? resolved.default : undefined,
  );
};

export const ifElseWrapperBuilder: MergeTagBuilder = (identifier, filters) => {
  const resolved = resolve(filters);
  const segments: string[] = [identifier];
  let elseBranch: string | undefined;

  for (const id of FILTER_ORDER) {
    const def = FILTER_REGISTRY[id];
    const value = resolved[id];
    if (value === undefined) continue;
    if (def.placement === "else") {
      elseBranch = typeof value === "string" ? value : "";
      continue;
    }
    const segment = def.build(value);
    if (segment) segments.push(segment);
  }
  segments.push(...(resolved.passthrough ?? []));

  const inner = `{{${segments.join("|")}}}`;
  if (nestedTileDataParentPaths(identifier).length > 0) {
    return wrapNestedTileDataAccess(identifier, inner, elseBranch);
  }
  return elseBranch === undefined
    ? inner
    : `{% if ${identifier} %}${inner}{% else %}${elseBranch}{% endif %}`;
};

/** The builder the whole app currently routes through. */
export const ACTIVE_BUILDER: MergeTagBuilder = liquidFilterChainBuilder;

/** Builds wire syntax for an identifier + optional filters via {@link ACTIVE_BUILDER}. */
export function build(identifier: string, filters?: ResolvedFilters): string {
  return ACTIVE_BUILDER(identifier, filters);
}
